import { api } from '@/api/cliente'
import { puenteMovil, type PuenteMovilPortal } from '@/lib/movil'
import {
  alCerrarSesion,
  cambiarSesion,
  restaurarSesion,
  sesionActual,
  suscribirse,
  type Sesion,
} from '@/dominio/sesion/sesion'

/**
 * Lo que el portal hace distinto dentro de la app «Agentical e-CF» de iOS y Android (movil/, ADR 0029).
 * En el navegador no hace nada: `puenteMovil()` es `null` y se sale en la primera línea.
 *
 * Tres cosas, y ninguna toca una pantalla:
 *
 * 1. **La sesión guardada.** Al abrir la app sin sesión en la pestaña, si hay una guardada tras la
 *    huella o la cara, se pide antes de pintar y se restaura. Al entrar con la contraseña, se ofrece
 *    guardarla (el puente pregunta una vez por cuenta). Se guarda el testigo, nunca la contraseña, y
 *    dura lo que dura el testigo.
 * 2. **Los avisos.** Con una sesión de contribuyente, el equipo se registra para los avisos de ese
 *    contribuyente (`PUT /interna/dispositivos/{instalacion}`), y al salir se da de baja en todos.
 * 3. **Tocar un aviso** lleva a su pantalla, cambiando antes de contribuyente si era de otro.
 */
export interface OpcionesMovil {
  navegar: (ruta: string) => void
  /** Lo cacheado de un contribuyente no puede pintarse bajo otro (`selectorDeSesion.ts`). */
  vaciarCache: () => void
}

/** El cuerpo de `PUT /interna/dispositivos/{instalacion}` (src/DevTek.Ecf.Api/Movil). */
interface RegistroDispositivo {
  app: 'portal'
  plataforma: 'ios' | 'android'
  proveedor: 'fcm' | 'apns'
  token: string
  versionApp: string
}

export async function prepararMovil(opciones: OpcionesMovil): Promise<void> {
  const movil = puenteMovil()
  if (movil === null) return

  if (sesionActual() === null) await abrirSesionGuardada(movil)

  let anterior = sesionActual()
  if (anterior !== null) void registrarEquipo(movil, anterior)

  suscribirse(() => {
    const ahora = sesionActual()
    const entrada = anterior === null
    const cambio = ahora !== null && ahora.testigo !== anterior?.testigo
    anterior = ahora
    if (ahora === null || !cambio) return

    // Solo al entrar con la contraseña. Al cambiar de contribuyente no: en Android guardar pide la
    // huella otra vez, y la guardada sigue sirviendo para volver a entrar.
    if (entrada && ahora.soporte === undefined) {
      void movil.sesionGuardada.ofrecer(JSON.stringify(ahora), ahora.expira.toISOString(), ahora.email).catch(() => false)
    }
    void registrarEquipo(movil, ahora)
  })

  alCerrarSesion((saliente) => {
    void movil.sesionGuardada.olvidar().catch(() => undefined)
    darDeBaja(movil, saliente)
  })

  movil.avisos.alTocar(({ ruta, tenant }) => void irAlAviso(ruta, tenant, opciones))
}

async function abrirSesionGuardada(movil: PuenteMovilPortal): Promise<void> {
  try {
    if ((await movil.sesionGuardada.consultar()) === null) return
    const datos = await movil.sesionGuardada.abrir()
    if (datos === null) return
    if (!restaurarSesion(JSON.parse(datos))) await movil.sesionGuardada.olvidar()
  } catch {
    // Lo guardado no se pudo leer: se entra con la contraseña, como siempre.
    await movil.sesionGuardada.olvidar().catch(() => undefined)
  }
}

/** Los contribuyentes en que este equipo ya quedó registrado en esta apertura de la app. */
const registrados = new Set<string>()

async function registrarEquipo(movil: PuenteMovilPortal, sesion: Sesion): Promise<void> {
  const tenant = sesion.activo?.tenant
  // Sin contribuyente (la sesión de un despacho) no hay de quién avisar; una de soporte es de un
  // operador de Agentical Group dentro de la cuenta de otro.
  if (!tenant || sesion.soporte !== undefined || !movil.avisos.disponibles) return

  const clave = `${sesion.email}|${tenant}`
  if (registrados.has(clave)) return

  const registro = await movil.avisos.registrar().catch(() => null)
  if (registro === null || !('token' in registro)) return

  const cuerpo: RegistroDispositivo = {
    app: 'portal',
    plataforma: movil.plataforma,
    proveedor: registro.proveedor,
    token: registro.token,
    versionApp: movil.version,
  }

  try {
    // PUT y no POST: registrar el mismo equipo dos veces es lo mismo que una (lo identifica la
    // instalación), así que la escritura es idempotente sin clave aparte.
    await api.reemplazar(`/interna/dispositivos/${encodeURIComponent(movil.avisos.instalacion())}`, { cuerpo })
    registrados.add(clave)
  } catch {
    // Se vuelve a intentar en la próxima entrada. Un aviso que no llega no detiene nada: el
    // tablero sigue diciendo lo mismo.
  }
}

/**
 * Al salir, este equipo deja de recibir avisos de esta cuenta en todos sus contribuyentes. Va con
 * el testigo de la sesión que se va —el cliente del portal ya no lo tiene—, y con `keepalive` para
 * que salga aunque la app se cierre enseguida. Con el testigo ya vencido no tiene sentido mandarlo.
 */
function darDeBaja(movil: PuenteMovilPortal, saliente: Sesion): void {
  if (!movil.avisos.disponibles || saliente.soporte !== undefined) return
  if (saliente.expira.getTime() <= Date.now()) return

  void fetch(`/interna/dispositivos/${encodeURIComponent(movil.avisos.instalacion())}?alcance=todos`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${saliente.testigo}` },
    keepalive: true,
  }).catch(() => undefined)
}

async function irAlAviso(ruta: string, tenant: string | null, opciones: OpcionesMovil): Promise<void> {
  const sesion = sesionActual()
  if (sesion === null) return

  if (tenant !== null && sesion.activo?.tenant !== tenant && sesion.disponibles.some((d) => d.tenant === tenant)) {
    try {
      await cambiarSesion({ tenant })
      opciones.vaciarCache()
    } catch {
      // Si ya no tiene acceso ahí, se queda donde estaba.
      return
    }
  }

  opciones.navegar(ruta)
}
